/**
 * @fileoverview Stage-aware status ordering (MOR-707)
 *
 * User-defined `taskStages` group statuses into an ordered pipeline (e.g.
 * "Backlog" holding several statuses, then "In flight"). Every surface that
 * lists statuses — board columns, status dropdowns, the statuses manager —
 * orders them stage-first (stage `sortOrder`, then the status's own
 * `sortOrder` within the stage), with ungrouped statuses trailing.
 */

export interface StageLite {
  _id: string;
  name: string;
  sortOrder: number;
}

interface StatusLite {
  sortOrder: number;
  stageId?: string | null;
}

/** Key used for the trailing "no stage" bucket. */
export const UNSTAGED_KEY = "__unstaged__";

/**
 * Group statuses under their stages, ordered stage-first. The trailing
 * bucket collects ungrouped statuses; its `label` is null when the org has
 * no stages at all (so callers can render a flat list).
 */
export function groupStatusesByStage<S extends StatusLite>(
  statuses: S[],
  stages: StageLite[],
  opts?: {
    /** Keep stages with no statuses (the settings manager shows them). */
    includeEmpty?: boolean;
  },
): Array<{ key: string; label: string | null; statuses: S[] }> {
  const orderedStages = [...stages].sort((a, b) => a.sortOrder - b.sortOrder);
  const byStage = new Map<string, S[]>();
  const unstaged: S[] = [];

  const stageIds = new Set(orderedStages.map((s) => s._id));
  for (const status of statuses) {
    const stageId = status.stageId ? String(status.stageId) : null;
    if (stageId && stageIds.has(stageId)) {
      const bucket = byStage.get(stageId) ?? [];
      bucket.push(status);
      byStage.set(stageId, bucket);
    } else {
      unstaged.push(status);
    }
  }

  const groups: Array<{ key: string; label: string | null; statuses: S[] }> =
    [];
  for (const stage of orderedStages) {
    const bucket = (byStage.get(stage._id) ?? []).sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
    if (bucket.length > 0 || opts?.includeEmpty) {
      groups.push({ key: stage._id, label: stage.name, statuses: bucket });
    }
  }
  if (unstaged.length > 0) {
    groups.push({
      key: UNSTAGED_KEY,
      label: orderedStages.length > 0 ? "Other" : null,
      statuses: unstaged.sort((a, b) => a.sortOrder - b.sortOrder),
    });
  }
  return groups;
}

/** Flatten to a single stage-ordered list (board columns, lookups). */
export function orderStatusesByStage<S extends StatusLite>(
  statuses: S[],
  stages: StageLite[],
): S[] {
  return groupStatusesByStage(statuses, stages).flatMap((g) => g.statuses);
}
